import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Crown, Sparkles, MessageCircle, ArrowLeft, Send } from 'lucide-react';
import { useRoom } from '../../../contexts/RoomContext';
import axios from 'axios';
import '../../auth/styles/ElegantLogin.css';

/**
 * コメント入力ページ
 * ユーザーがお祝いのメッセージを入力できるエレガントなフォーム
 */
const CommentInput: React.FC = () => {
  const [comment, setComment] = useState('');
  const [nickname, setNickname] = useState(localStorage.getItem('commentNickname') || '');
  const [color, setColor] = useState('#000000');
  const [fontSize, setFontSize] = useState(32);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const { roomId, isRoomSet, setRoomId } = useRoom();
  const navigate = useNavigate();

  const urlBase = process.env.REACT_APP_URL || 'http://localhost:8081';

  // roomidの復元処理（リロード対応）
  React.useEffect(() => {
    if (!isRoomSet || !roomId) {
      const savedRoomId = sessionStorage.getItem("roomId");
      if (savedRoomId) {
        setRoomId(savedRoomId);
      }
    }
  }, [isRoomSet, roomId, setRoomId]);

  // コメント送信処理
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!comment.trim()) {
      setMessage('コメントを入力してください。');
      return;
    }

    if (!nickname.trim()) {
      setMessage('ニックネームを入力してください。');
      return;
    }

    // roomIdが設定されていない場合はsessionStorageから復元を試みる
    let currentRoomId = roomId;
    if (!currentRoomId) {
      const savedRoomId = sessionStorage.getItem("roomId");
      if (savedRoomId) {
        setRoomId(savedRoomId);
        currentRoomId = savedRoomId;
      } else {
        setMessage('ルーム情報が取得できません。ゲストログインページからアクセスしてください。');
        return;
      }
    }

    setLoading(true);
    setMessage('');

    try {
      // ニックネームをローカルストレージに保存
      localStorage.setItem('commentNickname', nickname);

      const commentData = {
        roomid: currentRoomId,
        text: comment.trim(),
        nickname: nickname.trim(),
        userid: localStorage.getItem('userId') || null,
        color: color,
        fontSize: fontSize
      };

      console.log('[CommentInput] コメント投稿:', commentData);

      const token = localStorage.getItem("token");
      const headers: any = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.post(`${urlBase}/api/comment`, commentData, {
        headers
      });

      if (response.data.success) {
        console.log('[CommentInput] コメント投稿成功:', response.data);
        setMessage('コメントが正常に送信されました！スクリーンに表示されます。');
        // setComment('');
        
        // メッセージを3秒後にクリア
        setTimeout(() => {
          setMessage('');
        }, 3000);
        
      } else {
        console.error('[CommentInput] コメント投稿失敗:', response.data.message);
        setMessage(`コメント投稿に失敗しました: ${response.data.message}`);
      }
    } catch (error: any) {
      console.error('[CommentInput] コメント投稿エラー:', error);
      if (error.response?.data?.message) {
        setMessage(`エラー: ${error.response.data.message}`);
      } else {
        setMessage('コメントの送信に失敗しました。再度お試しください。');
      }
    } finally {
      setLoading(false);
    }
  };

  // メニューに戻る
  const handleBackToMenu = () => {
    navigate('/user/menu');
  };

  // カラーオプション（メジャーな色）
  const colorOptions = [
    { value: '#000000', label: '黒', preview: '#000000' },
    { value: '#FFFFFF', label: '白', preview: '#FFFFFF' },
    { value: '#FF0000', label: '赤', preview: '#FF0000' },
    { value: '#0000FF', label: '青', preview: '#0000FF' },
    { value: '#00FF00', label: '緑', preview: '#00FF00' },
    { value: '#FFFF00', label: '黄色', preview: '#FFFF00' },
    { value: '#FF00FF', label: 'ピンク', preview: '#FF00FF' },
    { value: '#FFA500', label: 'オレンジ', preview: '#FFA500' },
    { value: '#800080', label: '紫', preview: '#800080' }
  ];

  // フォントサイズオプション
  const fontSizeOptions = [
    { value: 21, label: '小' },
    { value: 32, label: '中' },
    { value: 48, label: '大' }
  ];

  return (
    <div className="elegant-login-container">
      <div className="elegant-login-background">
      </div>
      
      <div className="elegant-login-card">
        {/* ヘッダー装飾 */}
        <div className="elegant-login-header">
          <div className="header-decoration">
            <Crown className="crown-icon" />
            <Sparkles className="sparkle-icon sparkle-1" />
            <Sparkles className="sparkle-icon sparkle-2" />
          </div>
          <h1 className="elegant-login-title">
            <Heart className="title-heart" />
            Photo Memorial
            <Heart className="title-heart" />
          </h1>
          <p className="elegant-login-subtitle">Wedding Memory Sharing</p>
        </div>

        {/* コメント入力セクション */}
        <div className="elegant-form-section">
          <h2 className="elegant-form-title">
            <MessageCircle size={28} style={{ marginRight: '0.5rem', color: '#c7aa7a' }} />
            コメント投稿
          </h2>
          
          <div className="guest-message-section">
            <div className="guest-message-card">
              <p className="message-text">新郎新婦への</p>
              <p className="message-text highlight">お祝いのメッセージ</p>
              <p className="message-text">をお聞かせください</p>
            </div>
            
            <div className="guest-message-card">
              <p className="message-text">皆様からの温かいお言葉が</p>
              <p className="message-text">スクリーンに表示され</p>
              <p className="message-text">会場を素敵に彩ります</p>
            </div>
          </div>

          {/* コメント入力フォーム */}
          <form onSubmit={handleSubmit}>
            {/* ニックネーム入力 */}
            <div className="elegant-input-group" style={{ marginBottom: '1rem' }}>
              <MessageCircle className="input-icon" size={20} />
              <input
                type="text"
                className="elegant-input"
                placeholder="ニックネーム（20文字まで）"
                value={nickname}
                onChange={(e) => setNickname(e.target.value.slice(0, 20))}
                maxLength={20}
                disabled={loading}
                required
              />
            </div>

            {/* カスタマイズオプション */}
            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              marginBottom: '1rem',
              flexWrap: 'wrap'
            }}>
              {/* 色選択 */}
              <div style={{ flex: '1', minWidth: '150px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem',
                  color: '#8b7d6b',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}>
                  文字色
                </label>
                <select 
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '0.375rem',
                    border: '1px solid #d1c7b7',
                    backgroundColor: 'white',
                    fontSize: '0.875rem'
                  }}
                >
                  {colorOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* フォントサイズ選択 */}
              <div style={{ flex: '1', minWidth: '150px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem',
                  color: '#8b7d6b',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}>
                  文字サイズ
                </label>
                <select 
                  value={fontSize}
                  onChange={(e) => setFontSize(Number(e.target.value))}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '0.375rem',
                    border: '1px solid #d1c7b7',
                    backgroundColor: 'white',
                    fontSize: '0.875rem'
                  }}
                >
                  {fontSizeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* プレビュー */}
            <div style={{
              padding: '1rem',
              marginBottom: '1rem',
              borderRadius: '0.375rem',
              backgroundColor: '#f8f6f0',
              border: '1px solid #e5e0d3'
            }}>
              <div style={{ 
                fontSize: '0.875rem', 
                color: '#8b7d6b', 
                marginBottom: '0.5rem' 
              }}>
                プレビュー:
              </div>
              <div style={{
                color: color,
                fontSize: `${fontSize}px`,
                fontWeight: '600',
                fontFamily: '"Noto Sans JP", "Yu Gothic", "Hiragino Sans", sans-serif',
                textShadow: '0 1px 3px rgba(255, 255, 255, 0.8), 0 0 8px rgba(255, 255, 255, 0.6)',
                padding: '0.5rem',
                borderRadius: '0.25rem',
                display: 'inline-block',
                letterSpacing: '0.5px',
                backgroundColor: 'rgba(248, 243, 238, 0.9)',
                border: '1px solid rgba(200, 180, 160, 0.3)'
              }}>
                {comment || 'サンプルコメント'}
              </div>
            </div>

            <div className="elegant-input-group">
              <MessageCircle className="input-icon" size={20} />
              <textarea
                className="elegant-input comment-textarea"
                placeholder="お祝いのメッセージをお書きください..."
                value={comment}
                onChange={(e) => setComment(e.target.value.slice(0, 100))}
                maxLength={100}
                rows={6}
                disabled={loading}
                required
                style={{
                  resize: 'vertical',
                  minHeight: '120px',
                  paddingTop: '1rem',
                  lineHeight: '1.6'
                }}
              />
            </div>

            {/* 文字数カウンター */}
            <div style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '1.5rem',
              color: '#8b7d6b',
              fontSize: '0.875rem',
              fontFamily: 'Inter, sans-serif'
            }}>
              <span>ニックネーム: {nickname.length}/20文字</span>
              <span>コメント: {comment.length}/100文字</span>
            </div>

            {/* 送信ボタン */}
            <button 
              type="submit"
              className="elegant-login-button"
              disabled={loading || !comment.trim() || !nickname.trim()}
            >
              {loading ? (
                <>
                  <div className="elegant-loading-spinner"></div>
                  送信中...
                </>
              ) : (
                <>
                  <Send size={20} />
                  コメントを送信
                </>
              )}
            </button>

            {/* メッセージ表示 */}
            {message && (
              <div className={`elegant-message ${message.includes('成功') ? 'success-message' : ''}`}>
                <p>{message}</p>
              </div>
            )}
          </form>

          {/* バックボタン */}
          <button 
            onClick={handleBackToMenu}
            className="elegant-guest-button secondary"
            disabled={loading}
            style={{ marginTop: '1rem' }}
          >
            <ArrowLeft size={20} />
            メニューに戻る
          </button>
        </div>

        {/* フッター装飾 */}
        <div className="elegant-login-footer">
          <div className="footer-decoration">
            <div className="decoration-line"></div>
            <Heart className="footer-heart" />
            <div className="decoration-line"></div>
          </div>
          <p className="footer-text">Share Your Heartfelt Wishes</p>
        </div>
      </div>
    </div>
  );
};

export default CommentInput;